import { useEffect, useState, useCallback } from 'react';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Box, FormControl, InputLabel, MenuItem, Select, Typography, Button, Tooltip, Paper } from '@mui/material';
import Swal from 'sweetalert2';
import { useTranslation } from 'react-i18next';
import { SCUIButton } from '../../../../atomic/atoms';
import { SCUICustomDataGrid } from '../../../../atomic/molecules';
import TimeHelper from '../../../../utils/time';
import { downloadExcel } from '../../../../utils/excelFileHelper';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import ShippingQuery from '../../../../services/query/shipping/ShippingQuery.services';
import ShippingCommand from '../../../../services/command/shipping/ShippingCommand.services';
import { SCUINoRecordComponent } from '../../../../atomic/atoms';
import FeeManagementListDrawer from './components/FeeManagementListDrawer';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { MPCFM } from '../../mpCargoFeeManagement.classes';
import { mapSellerShipmentCosts } from './feeManagementListTab.helpers';

type PaginationModel = {
    pageSize: number;
    pageNumber: number; // 1-based (API)
    totalCount: number;
};

interface FeeRowItem {
    id: number;
    shippingCompanyId: number;
    shippingCompanyName: string;
    desi: number | '';
    cost: number | '';
    startDate: string | Date | null;
    expirationDate: string | Date | null;
    lastModifiedDate: string | Date | null;
    lastModifiedBy: string;
    lastModifiedByName: string;
}

interface CargoCompanyOption {
    id: number;
    name: string;
}
export const FeeManagementListTab = ({
    setIsLoading,
    companyOptions,
}: {
    setIsLoading: (isLoading: boolean) => void;
    companyOptions: CargoCompanyOption[];
}): React.JSX.Element => {
    const { t } = useTranslation();

    const [rows, setRows] = useState<FeeRowItem[]>([]);
    const [drawerOpen, setDrawerOpen] = useState<boolean>(false);
    const [isEditMode, setIsEditMode] = useState<boolean>(false);
    const [formState, setFormState] = useState<FeeRowItem | null>(null);

    const [filterCompanyId, setFilterCompanyId] = useState<number>(-1);
    const [hasFiltered, setHasFiltered] = useState<boolean>(false);
    const [pagination, setPagination] = useState<PaginationModel>({
        pageSize: 10,
        pageNumber: 1,
        totalCount: 0,
    });

    const initialFormState = () => ({
        id: 0,
        shippingCompanyId: filterCompanyId || 0,
        shippingCompanyName: '',
        desi: 0,
        cost: 0,
        startDate: TimeHelper.formatDate(new Date()),
        expirationDate: TimeHelper.formatDate(new Date()),
        lastModifiedDate: null,
        lastModifiedBy: '',
        lastModifiedByName: '',
    });

    const fetchList = useCallback(() => {
        setIsLoading(true);
        const companyParam = {
            ShippingCompanyId: filterCompanyId,
            pageNumber: pagination.pageNumber,
            pageSize: pagination.pageSize,
        };
        ShippingQuery.GetSellerShipmentCosts(companyParam)
            .then((res: any) => {
                const data = res?.data?.sellerShipmentCosts || [];
                const dataWithId = mapSellerShipmentCosts(data, companyOptions);
                setRows(dataWithId);
                setPagination((prev) => ({
                    ...prev,
                    totalCount: Number(res?.data?.totalCount || 0),
                }));
            })
            .catch(() => {
                Swal.fire(t('general.error'), '', 'error');
            })
            .finally(() => setIsLoading(false));
    }, [pagination.pageNumber, pagination.pageSize, filterCompanyId]);

    useEffect(() => {
        if (!hasFiltered) return;
        fetchList();
    }, [hasFiltered, fetchList, pagination.pageNumber, pagination.pageSize]);

    const resetForm = () => {
        setFormState(initialFormState());
    };

    const handleOpenAdd = () => {
        setIsEditMode(false);
        resetForm();
        setDrawerOpen(true);
    };

    const handleOpenEdit = (row: FeeRowItem) => {
        setIsEditMode(true);
        setFormState({ ...row, shippingCompanyId: filterCompanyId || 0 });
        setDrawerOpen(true);
    };

    const toMMDDYYYY = (val?: string | Date | null): string => {
        if (!val) return '';
        let d: Date;
        if (val instanceof Date) {
            d = val;
        } else {
            const s = String(val).trim();
            if (s.includes('T')) {
                d = new Date(s);
            } else {
                const parts = s.replace(/[.\\-]/g, '/').split('/');
                if (parts.length === 3) {
                    const [a, b, c] = parts.map(Number);
                    d = a > 31 ? new Date(a, b - 1, c) : new Date(c, b - 1, a);
                } else {
                    d = new Date(s);
                }
            }
        }
        if (Number.isNaN(d.getTime())) return '';
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const dy = String(d.getDate()).padStart(2, '0');
        const y = d.getFullYear();
        return `${m}/${dy}/${y}`;
    };

    const handleDelete = async (row: FeeRowItem) => {
        const result = await Swal.fire({
            title: t('general.warning'),
            text: t('general.deleteConfirmation'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: t('general.ok'),
            cancelButtonText: t('general.cancel'),
        });

        if (!result.isConfirmed) return;

        const payload = {
            shippingCompanyId: Number(row.shippingCompanyId),
            desi: Number(row.desi),
            startDate: toMMDDYYYY(row.startDate),
            expirationDate: toMMDDYYYY(row.expirationDate),
        };

        setIsLoading(true);
        try {
            const res: any = await ShippingCommand.DeleteSellerShipmentCosts(payload);
            if (!res?.isSuccess) {
                Swal.fire({
                    title: t('general.error'),
                    text: res?.message || t('general.anErrorOccured'),
                    icon: 'error',
                    confirmButtonText: t('general.ok'),
                });
                return;
            }

            Swal.fire({
                title: t('general.success'),
                icon: 'success',
                timer: 1500,
                showConfirmButton: false,
            });
            fetchList();
        } catch {
            Swal.fire({
                title: t('general.error'),
                text: t('general.anErrorOccured'),
                icon: 'error',
                confirmButtonText: t('general.ok'),
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handleCloseDrawer = () => {
        setDrawerOpen(false);
        resetForm();
    };

    const handleExportExcel = () => {
        const excelRows = rows.map((r) => ({
            shippingCompanyName: r.shippingCompanyName,
            desi: r.desi,
            cost: r.cost,
            startDate: TimeHelper.formatDate(r.startDate),
            endDate: TimeHelper.formatDate(r.expirationDate),
            changedAt: TimeHelper.formatDate(r.lastModifiedDate),
            changedBy: r.lastModifiedByName,
        }));
        const now = new Date();
        const dd = String(now.getDate()).padStart(2, '0');
        const mm = String(now.getMonth() + 1).padStart(2, '0');
        const yyyy = now.getFullYear();
        const fileName = `MPKargoÜcretYönetimi_${dd}.${mm}.${yyyy}.xlsx`;
        downloadExcel({
            excelDownloadData: excelRows,
            excelDownloadFolderName: 'MP-Cargo-Fee-Management',
            fileNameOverride: fileName,
        });
    };

    const columns = [
        {
            field: 'id',
            headerName: t('mpCargoFeeManagement.list.columns.id'),
            flex: 0.5,
            hide: true,
        },
        {
            field: 'shippingCompanyName',
            headerName: t('mpCargoFeeManagement.list.columns.cargoCompany'),
            flex: 1,
        },
        {
            field: 'desi',
            headerName: t('mpCargoFeeManagement.list.columns.desi'),
            flex: 0.7,
        },
        {
            field: 'cost',
            headerName: t('mpCargoFeeManagement.list.columns.cost'),
            width: 140,
            renderHeader: () => (
                <Box className={MPCFM.listTabCostHeader} sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <Typography component="span">{t('mpCargoFeeManagement.list.columns.cost')}</Typography>
                    <Tooltip title={t('mpCargoFeeManagement.list.columns.costInfo')} placement="top">
                        <InfoOutlinedIcon className={MPCFM.inputIconCostInfo} sx={{ fontSize: 16, color: 'text.secondary' }} />
                    </Tooltip>
                </Box>
            ),
            renderCell: (params: any) => <Typography>{`${params.row.cost} ₺`}</Typography>,
        },
        {
            field: 'startDate',
            headerName: t('mpCargoFeeManagement.list.columns.startDate'),
            flex: 1,
            renderCell: (params: any) => <Typography>{TimeHelper.formatDate(params.row.startDate)}</Typography>,
        },
        {
            field: 'expirationDate',
            headerName: t('mpCargoFeeManagement.list.columns.expirationDate'),
            flex: 1,
            renderCell: (params: any) => <Typography>{TimeHelper.formatDate(params.row.expirationDate)}</Typography>,
        },
        {
            field: 'lastModifiedDate',
            headerName: t('mpCargoFeeManagement.list.columns.changedAt'),
            flex: 1,
            renderCell: (params: any) => <Typography>{TimeHelper.formatDate(params.row.lastModifiedDate)}</Typography>,
        },
        {
            field: 'lastModifiedByName',
            headerName: t('mpCargoFeeManagement.list.columns.changedBy'),
            flex: 1,
        },
        {
            field: 'actions',
            headerName: t('mpCargoFeeManagement.list.columns.edit'),
            flex: 0.8,
            renderCell: (params: any) => (
                <Box className={MPCFM.listTabDatagridActionsCell} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <Button
                        className={MPCFM.inputButtonRowEdit}
                        onClick={() => handleOpenEdit(params.row)}
                        sx={{
                            minWidth: '30px !important',
                            width: '30px !important',
                            minHeight: '30px !important',
                            height: '30px !important',
                            backgroundColor: '#00B8F4',
                            borderRadius: 1.5,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: 'none',
                            p: 0,
                            m: 0,
                            '&:hover': {
                                backgroundColor: '#0099c8',
                                boxShadow: 'none',
                            },
                        }}
                    >
                        <EditIcon sx={{ color: 'white', fontSize: 14 }} />
                    </Button>
                    <Button
                        className={MPCFM.inputButtonRowDelete}
                        onClick={() => handleDelete(params.row)}
                        sx={{
                            minWidth: '30px !important',
                            width: '30px !important',
                            minHeight: '30px !important',
                            height: '30px !important',
                            backgroundColor: '#f44336',
                            borderRadius: 1.5,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: 'none',
                            p: 0,
                            m: 0,
                            '&:hover': {
                                backgroundColor: '#d32f2f',
                                boxShadow: 'none',
                            },
                        }}
                    >
                        <DeleteIcon sx={{ color: 'white', fontSize: 14 }} />
                    </Button>
                </Box>
            ),
        },
    ];
    console.log(filterCompanyId);
    if (!hasFiltered) {
        return (
            <Box className={MPCFM.listTabEmptyGate} sx={{ width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 4 }}>
                <Paper
                    className={MPCFM.listTabEmptyPaper}
                    sx={{ p: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, width: 420 }}
                >
                    <SCUINoRecordComponent />
                    <FormControl className={MPCFM.listTabEmptyCompanyField} fullWidth>
                        <InputLabel id="filter-company-label">
                            {t('mpCargoFeeManagement.list.columns.cargoCompany')}
                        </InputLabel>
                        <Select
                            className={MPCFM.inputSelectListTabCompany}
                            labelId="filter-company-label"
                            label={t('mpCargoFeeManagement.list.columns.cargoCompany')}
                            value={filterCompanyId}
                            onChange={(e) => setFilterCompanyId(Number(e.target.value))}
                        >
                            {(companyOptions || []).map((opt) => (
                                <MenuItem key={opt.id} value={opt.id}>
                                    {opt.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <SCUIButton
                        className={MPCFM.inputButtonListTabFilter}
                        text={t('general.filter')}
                        type="primary"
                        disabled={filterCompanyId === -1}
                        sizeType="fixed"
                        size={'m'}
                        onClick={() => {
                            if (filterCompanyId !== -1) {
                                setHasFiltered(true);
                            }
                        }}
                    />
                </Paper>
            </Box>
        );
    }

    return (
        <Box className={MPCFM.listTabRoot} sx={{ width: '100%' }}>
            <Box className={MPCFM.listTabToolbarBack} sx={{ display: 'flex', justifyContent: 'flex-start', gap: '8px', marginBottom: '12px' }}>
                <SCUIButton
                    className={MPCFM.inputButtonListTabBack}
                    text={t('general.back')}
                    type="secondary"
                    sizeType="fixed"
                    style={{ height: '35px' }}
                    size={'m'}
                    buttonType="button"
                    onClick={() => setHasFiltered(false)}
                    showStartIcon
                    startIcon={<ArrowBackIcon sx={{ color: '#0d6efd' }} />}
                />
            </Box>
            <Box className={MPCFM.listTabToolbarActions} sx={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginBottom: '12px' }}>
                <SCUIButton
                    className={MPCFM.inputButtonListTabAdd}
                    text={t('mpCargoFeeManagement.list.addNew')}
                    type="primary"
                    sizeType="fixed"
                    style={{ height: '35px' }}
                    size={'m'}
                    buttonType="button"
                    onClick={handleOpenAdd}
                    showStartIcon
                    startIcon={<AddIcon sx={{ color: '#fff' }} />}
                />
                <SCUIButton
                    className={MPCFM.inputButtonListTabExportExcel}
                    text={t('general.exportToExcel')}
                    type="secondary"
                    sizeType="fixed"
                    style={{ height: '35px' }}
                    size={'m'}
                    buttonType="button"
                    onClick={handleExportExcel}
                    showStartIcon
                    startIcon={<FileDownloadIcon sx={{ color: '#0d6efd' }} />}
                />
            </Box>

            <Box className={MPCFM.listTabGridWrap}>
                <SCUICustomDataGrid
                    rows={rows}
                    columns={columns}
                    paginationModeChange
                    rowCount={pagination.totalCount}
                    page={pagination.pageNumber - 1}
                    pageSize={pagination.pageSize}
                    onPageChange={(newPage: any) => {
                        setPagination((prev) => ({ ...prev, pageNumber: newPage + 1 }));
                    }}
                    onPageSizeChange={(newPageSize: number) => {
                        setPagination((prev) => ({
                            ...prev,
                            pageSize: newPageSize,
                            pageNumber: 1,
                        }));
                    }}
                />
            </Box>

            <FeeManagementListDrawer
                open={drawerOpen}
                onClose={handleCloseDrawer}
                companyOptions={companyOptions}
                filterCompanyId={filterCompanyId}
                initialData={isEditMode ? (formState as any) : undefined}
                setIsLoading={setIsLoading}
                onSaved={fetchList}
            />
        </Box>
    );
};
